import { useState, useEffect } from "react";
import { useSetRecoilState, useRecoilValue } from "recoil";
import {
  TournamentResponse,
  getStructuresByCafeId,
  TimerDto,
  removeTournament,
} from "../../../api/tournament";
import { removeTimer } from "../../../api/tournament-timer";
import { PaginationWrapper } from "../../../component/CommonTable";
import { dialogState } from "../../../recoil/dialog.atom";
import { pubState } from "../../../recoil/pub.atom";
import {
  NormalTimerPopup,
  PageContentHeader,
  PageHeader,
  PageWrapper,
} from "../../../styles/page";
import { Overlay } from "../../../styles/popup";
import { getResultTime, isRegistrationAvailable } from "../../../utils/timer";
import { calculateHomeLevel } from "../../timer-controller/hooks/timer";
import EditTournamentPopup from "../components/edit";
import { TournamentProvider } from "../hook/context";
import { StructuresResponse, TournamentType } from "../hook/useEditTournament";
import { EndPopup } from "../style";
import { MainWrapper, MainHeaderActionsWrapper } from "../style/home";
import Pagination from "../../../component/Pagination";

const PcTournamentList = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [list, setList] = useState<TournamentResponse[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number[]>([]);
  const setDialog = useSetRecoilState(dialogState);
  const pub = useRecoilValue(pubState);
  const [moreIndex, setMoreIndex] = useState<number | null>(null);
  const [isEnd, setIsEnd] = useState<boolean>(false);
  const [isNormal, setIsNormal] = useState<boolean>(false);
  const [overTournament, setOverTournament] = useState<number[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const [filterType, setFilterType] = useState<string>("all");
  const limit = 10;

  const filter = [
    {
      name: "전체",
      value: "all",
    },
    {
      name: "진행중",
      value: "progress",
    },
    {
      name: "시작 준비 중",
      value: "starting",
    },
    {
      name: "종료",
      value: "end",
    },
  ];

  const getList = async () => {
    if (!pub) {
      return;
    }

    const res = await getStructuresByCafeId(
      pub.id,
      currentPage,
      limit,
      "SOLUTION",
      filterType,
    );
    setTotalPage(Math.ceil(res.total / limit));
    setList(res.tournaments);
  };

  const onPageChange = (nextPage: number) => {
    setCurrentPage(nextPage);
  };

  useEffect(() => {
    getList();
  }, [currentPage, filterType]);

  const [isWrite, setIsWrite] = useState(false);

  const getLevel = (timerDto: TimerDto, levels: StructuresResponse[]) => {
    const now = new Date();
    const updatedAt = new Date(timerDto.updatedAt);
    const elapsedTimeMs =
      now.getTime() - updatedAt.getTime() + timerDto.playTime;

    const { currentLevel } = calculateHomeLevel(elapsedTimeMs, levels);

    return currentLevel;
  };

  const [editId, setEditId] = useState<number | null>(null);

  const handleSelect = (id: number) => {
    setSelectedIndex((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const getPrize = (item: TournamentResponse) => {
    if (item.moneyInChip) {
      const total = item.moneyInChip.reduce(
        (total: number, chip: { point: number }) => {
          return total + chip.point;
        },
        0,
      );
      return total;
    }
    return 0;
  };

  const delayTournament = () => {
    list.forEach((item) => {
      if (item.timerDto?.isStop) {
        const startDate = new Date(item.startDate);

        const currentTime = new Date();

        const timeDifference = currentTime.getTime() - startDate.getTime();
        const oneDayInMilliseconds = 24 * 60 * 60 * 1000;

        if (timeDifference >= oneDayInMilliseconds) {
          if (!item.timerDto) return;
          if (item.userCount > 0 || item.reservationCount > 0) return;
          const id = item.timerDto.id;
          setIsEnd(true);
          setOverTournament((prev) => [...prev, id]);
        }
      }
    });
  };

  const handleEnd = async () => {
    try {
      await Promise.all(
        overTournament.map(async (item) => {
          await removeTimer(item);
        }),
      );

      setOverTournament([]);
      setIsEnd(false);
      await getList();
    } catch (e: any) {
      alert(e.message);
    }
  };

  useEffect(() => {
    delayTournament();
  }, [list]);

  const getTournamentStateText = (item: TournamentResponse) => {
    if (!item.timerDto) {
      return item.isEnd ? "종료" : "시작 준비 중";
    }
    if (item.timerDto) {
      if (item.timerDto.isStop) return "지연";
      if (item.structure) {
        if (getResultTime(item) < 0) return "시작 준비 중";
        return getLevel(item.timerDto, item.structure)?.level
          ? "LV " + getLevel(item.timerDto, item.structure)?.level
          : "BREAK";
      }
    }
    return "종료";
  };

  const getTournamentStateClassName = (item: TournamentResponse) => {
    if (!item.timerDto) {
      return item.isEnd ? "end" : "starting";
    }
    if (item.timerDto) {
      if (item.timerDto.isStop) return "stop";
      if (item.structure) {
        if (getResultTime(item) < 0) return "starting";
        return "lv";
      }
    }
    return "end";
  };

  return (
    <>
      <PageWrapper>
        <PageHeader>토너먼트</PageHeader>
        {isWrite && (
          <TournamentProvider>
            <EditTournamentPopup
              onClose={() => setIsWrite(false)}
              tournamentId={editId}
              getData={getList}
            />
          </TournamentProvider>
        )}
        {isEnd && (
          <EndPopup>
            <div className="content">
              24시간 전에 <br />
              일시정지 된 토너먼트가 존재합니다. <br />
              종료하시겠습니까?
            </div>
            <div className="button-group">
              <div className="button purple" onClick={() => handleEnd()}>
                예
              </div>
              <div className="button" onClick={() => setIsEnd(false)}>
                아니오
              </div>
            </div>
          </EndPopup>
        )}
        {isNormal && (
          <NormalTimerPopup>
            <div className="content">
              *<span>일반 타이머</span>는 싯인 기능을 사용할 수 없습니다. <br />
              토너먼트 종료 후 정산페이지 <span>
                [매장이용권 수동 지급]
              </span>을 <br />
              통해 <span>매장이용권를 지급</span>해 주세요.
            </div>
            <div className="button" onClick={() => setIsNormal(false)}>
              확인
            </div>
          </NormalTimerPopup>
        )}
        <MainWrapper>
          <PageContentHeader>
            <div className="title">
              <div className={`tap selected`}>솔루션 타이머</div>
            </div>
            <MainHeaderActionsWrapper>
              <div
                className="drop-box-wrapper"
                onClick={() => {
                  setIsFilterOpen(!isFilterOpen);
                }}
              >
                <div className="value">
                  {filter.map((type, index) =>
                    type.value === filterType ? type.name : "",
                  )}
                </div>
                <img src="/image-web/dashboard/reservation/drop.svg" />
                {isFilterOpen && (
                  <div className="drop-box">
                    {filter.map((type, index) => (
                      <div
                        key={index}
                        className={`item ${
                          filterType === type.value && "active"
                        }`}
                        onClick={() => {
                          setCurrentPage(1);
                          setFilterType(type.value);
                        }}
                      >
                        {type.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div
                className="add-button"
                onClick={() => {
                  setEditId(null);
                  setIsWrite(true);
                }}
              >
                토너먼트 추가하기
              </div>
            </MainHeaderActionsWrapper>
          </PageContentHeader>
          <div className="table-wrapper">
            <div className="header">
              <div className="item a">상태</div>
              <div className="item b">시작 시간</div>
              <div className="item c">PRIZE</div>
              <div className="item d">BUY-IN</div>
              <div className="item e">토너먼트 이름</div>
              <div className="item f">플레이어</div>
              <div className="item g">예약 상태</div>
              <div className="item h">타이머 컨트롤러</div>
              <div className="item i" />
            </div>
            <div className="list-wrapper">
              {list.map((item, index) => (
                <div
                  className={`data-wrapper ${
                    selectedIndex.includes(item.id) && "selected"
                  }`}
                  key={index}
                >
                  <div className="item a">
                    <span
                      className={`des ${getTournamentStateClassName(item)}`}
                    >
                      {getTournamentStateText(item)}
                    </span>
                  </div>
                  <div className="item b">{item.startDate}</div>
                  <div className="item c">
                    {item.tournamentType === TournamentType.Tournament
                      ? getPrize(item) >= 10000
                        ? item.prizeType === "ALL"
                          ? "복합"
                          : getPrize(item) / 10000 +
                            "(만)" +
                            (item.prizeType === "SEED"
                              ? "RABBIT"
                              : item.prizeType)
                        : getPrize(item).toLocaleString() +
                          (item.prizeType === "SEED"
                            ? "RABBIT"
                            : item.prizeType)
                      : item.tournamentType === TournamentType.Daily
                        ? item.moneyInChip.reduce((total, chip) => {
                            if (total + chip.point >= 10000) {
                              return total + chip.point / 10000;
                            }
                            return total + chip.point;
                          }, 0) + "%"
                        : "0"}
                  </div>
                  <div className="item d">
                    {item.buyIn ? item.buyIn.toLocaleString() : "0"}
                  </div>
                  <div className="item e">{item.title}</div>
                  <div className="item f">{`${item.userCount} / ${
                    item.adOnUserCount +
                    item.reBuyInUserCount +
                    item.entryUserCount
                  }`}</div>
                  <div className="item g">
                    {isRegistrationAvailable(item) ? "레지 가능" : "레지 마감"}
                  </div>
                  <div className="item h">
                    {selectedIndex.includes(item.id) ? (
                      <img
                        src="/image-web/tournament/game-timer-selected.svg"
                        onClick={() => {
                          window.open(
                            `/timer-config/${item.id}`,
                            "_blank",
                            "noopener,noreferrer",
                          );
                        }}
                        alt="game-timer"
                      />
                    ) : (
                      <img
                        src="/image-web/tournament/game-timer.svg"
                        onClick={() => {
                          window.open(
                            `/timer-config/${item.id}`,
                            "_blank",
                            "noopener,noreferrer",
                          );
                        }}
                        alt="game-timer"
                      />
                    )}
                  </div>
                  <div className="item i">
                    <img
                      src="/image-web/tournament/more.svg"
                      onClick={() => {
                        if (moreIndex !== index) {
                          setMoreIndex(index);
                        } else {
                          setMoreIndex(null);
                        }
                      }}
                      alt="more"
                    />
                    {moreIndex === index && (
                      <div className="drop-box">
                        <div
                          className="item"
                          onClick={() => {
                            setEditId(item.id);
                            setIsWrite(true);
                            setMoreIndex(null);
                          }}
                        >
                          수정하기
                        </div>
                        {item.entryUserCount <= 0 && (
                          <div
                            className="item"
                            onClick={() => {
                              setDialog({
                                title: "정말 삭제하시겠습니까?",
                                confirm: async () => {
                                  try {
                                    if (selectedIndex.includes(item.id)) {
                                      handleSelect(item.id);
                                    }
                                    if (item.reservationCount > 0) {
                                      alert(
                                        "예약자가 존재해 토너먼트를 삭제할 수 없습니다.",
                                      );
                                      return;
                                    }
                                    await removeTournament(item.id);
                                    await getList();
                                  } catch (e: any) {
                                    alert(e.message);
                                    return;
                                  }
                                },
                              });
                              setMoreIndex(null);
                            }}
                          >
                            삭제하기
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          {totalPage > 1 && (
            <PaginationWrapper>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPage}
                onPageChange={onPageChange}
              />
            </PaginationWrapper>
          )}
        </MainWrapper>
        {(isEnd || isNormal) && <Overlay />}
      </PageWrapper>
    </>
  );
};

export default PcTournamentList;
